import { UseCaseStep } from "@models/useCaseStep.model.ts";
import { CommandCreateEngagement } from "./commands/cmdCreateEngagement";
import { CommandRequestContent, type RequestContentOpts } from "./commands/cmdRequestContent";
import { CommandCreateObjective } from "./commands/cmdCreateObjective";
import { CommandEngagementDelete } from "./commands/cmdEngagementDelete";
import { CommandRecordEngagementView } from "./commands/cmdRecordEngagementView"
import { CommandGetEngagementsList, type EngagementListQuery } from "./commands/cmdEngagementList";
import { CommandMotimaticUsers } from "./commands/cmdMotimaticUsers";
import { CommandGetObjectivesData } from "./commands/cmdGetObjectivesData";
import { CommandGetObjective } from "./commands/cmdGetObjective";
import { CommandGetObjectiveListTypeChoices } from "./commands/cmdGetObjectiveListTypeChoices";
import { CommandGetReferenceDataGroup } from "./commands/cmdGetReferenceDataGroup";
import { CommandObjectiveDelete } from "./commands/cmdObjectiveDelete";
import { CommandUpdateEngagement } from "./commands/cmdUpdateEngagement";
import { CommandUseCaseSteps } from "./commands/cmdUseCaseSteps";
import { CommandLookUpList } from "./commands/cmdLookUpList";
import { CommandLaunches } from "./commands/cmdLaunches";
import { CommandGetEngagement } from "./commands/cmdGetEngagement";
import {
  CommandGetEngagementActivity,
  CommandGetEngagementVersionSnapshot,
  CommandCountEngagementVersions,
  CommandListEngagementVersions,
} from "./commands/cmdListEngagementVersions";
import { CommandGetCampaignTable } from "./commands/cmdGetCampaignTable";
import { CommandGetEngagementAdTags } from "./commands/cmdGetEngagementAdTags";
import { CommandGetCampaignAudiences } from "./commands/cmdGetCampaignAudiences";
import { CommandUpdateObjective } from "./commands/cmdUpdateObjective";
import { CommandUpdateEngagementData } from "./commands/cmdUpdateEngagementData";
import { CommandGetEngagementAds } from "./commands/cmdGetEngagementAds";
import {
  CommandUpdateEngagementAdStatus,
  EngagementAdStatus,
} from "./commands/cmdUpdateEngagementAdStatus";
import { CommandCreateTeamMember } from "./commands/cmdCreateTeamMember";
import { CommandAssignTeamMemberToEngagement } from "./commands/cmdAssignTeamMemberToEngagement";
import { CommandGetTeamRoles } from "./commands/cmdGetTeamroles";
import { CommandGetTeamMembers } from "./commands/cmdGetTeamMembers";
import { CommandUpdateTeamMember } from "./commands/cmdUpdateTeamMember";
import { CommandDeleteTeamMember } from "./commands/cmdDeleteTeamMember";
import { CommandUpdateTeamMemberProfilePicture } from "./commands/cmdUpdateTeamMemberProfilePicturey";
import { Objective } from "@models/objective.model";
import { KeyDate } from "@models/keyDate.model";
import { CommandCreateKeyDate } from "./commands/cmdCreateKeyDate";
import { CommandGetKeyDateTypes } from "./commands/cmdGetKeyDateTypes";
import { CommandGetKeyDate } from "./commands/cmdGetKeyDate";
import { CommandDeleteKeyDate } from "./commands/cmdDeleteKeyDate";
import { CommandUpdateKeyDates } from "./commands/cmdUpdateKeyDates";
import { CommandCreateObjectiveJourneySteps } from "./commands/cmdCreateObjectiveJourneySteps";
import { CommandGetObjectiveJourneySteps } from "./commands/cmdGetObjectiveJourneySteps";
import { CommandDeleteObjectiveJourneyStep } from "./commands/cmdDeleteObjectiveJourneyStep";
import { CommandEditObjectiveJourneySteps } from "./commands/cmdEditObjectiveJourneyStep";
import { CommandGetEngagementLaunches } from "./commands/cmdGetEngagementLaunches";
import { CommandCreateEngagementLaunch } from "./commands/cmdCreateEngagementLaunch";
import { CommandSetLaunchPopulationSegment } from "./commands/cmdSetLaunchPopulationSegment";
import { CommandSetLaunchGoLiveDate } from "./commands/cmdSetLaunchGoLiveDate";
import { CommandGetSegmentLinks } from "./commands/cmdGetSegmentLinks";
import { CommandGetEngagementAnalytics, type AnalyticsScope } from "./commands/cmdGetEngagementAnalytics";
import { CommandGetEngagementMetrics } from "./commands/cmdGetEngagementMetrics"
import { CommandGetEngagementLaunchAnalytics } from "./commands/cmdGetEngagementLaunchAnalytics"
import { CommandUpdateEngagementName } from "./commands/cmdUpdateEngagementName"
import { CommandGetEngagementStatus } from "./commands/cmdGetEngagementStatus";
import { CommandUpdateEngagementStatus } from "./commands/cmdUpdateEngagementStatus";
import { CommandCloseEngagement } from "./commands/cmdCloseEngagement";
import { CommandArchiveEngagement } from "./commands/cmdArchiveEngagement";
import { CommandSetEngagementArchived } from "./commands/cmdSetEngagementArchived";

import { CommandGetEngagementTree } from "./commands/cmdGetEngagementTree";
import type { EngagementTreeResponse } from "./commands/cmdGetEngagementTree";
import { CommandPublishEngagement, PublishOptions } from "./commands/cmdPublishEngagement";
import {
  CommandGetPublishControl,
  CommandSetLaunchReady,
  CommandGetPublishHistory,
} from "./commands/cmdPublishControl";
import { CommandSyncEngagement } from "./commands/cmdSyncEngagement";
import { CommandNetworkViewEngagement } from "./commands/cmdNetworkViewEngagement";
import { CommandGetNetworkView } from "./commands/cmdGetNetworkView";
import type { EngagementNetworkViewResponse } from "./commands/cmdGetNetworkView";
import { CommandImportPerformance } from "./commands/cmdImportPerformance";
import { CommandGetLastPerformanceImport, LastPerformanceImport } from "./commands/cmdGetLastPerformanceImport";
import {
  CommandGetEngagementSetupStatus,
  SetupStatusResponse,
} from "./commands/cmdGetEngagementSetupStatus";
import { addJob } from "@store/jobTrackerStore";
export class EngagementService {
  async createEngagement(
    provider_id: number,
    client_id: number,
    type: string,
    opts: { name?: string; adIds?: number[]; additionalNotes?: string } = {},
  ) {
    const cmd = new CommandCreateEngagement()
    const results = await cmd.run(provider_id, client_id, type, opts)
    return results
  }

  async requestContent(
    provider_id: number,
    client_id: number,
    opts: RequestContentOpts & { name?: string } = {},
  ) {
    return new CommandRequestContent().run(provider_id, client_id, opts);
  }

  async deleteEngagement(engagementId: number) {
    const cmd = new CommandEngagementDelete()
    const results = await cmd.run(engagementId)
    return results
  }

  async updateEngagement(engagementId: number, organizationId: number) {
    const cmd = new CommandUpdateEngagement()
    const results = await cmd.run(engagementId, organizationId)
    return results
  }

  async getEngagement(engagementId: number) {
    const cmd = new CommandGetEngagement()
    const results = await cmd.run(engagementId)
    return results
  }

  async updateEngagementName(engagementId: number, newEngagementName: string) {
    const cmd = new CommandUpdateEngagementName()
    const results = await cmd.run(engagementId, newEngagementName)
    return results
  }

  async getEngagementsList(
    providerId: number,
    limit: number,
    offset: number,
    query: EngagementListQuery = {},
  ) {
    const cmd = new CommandGetEngagementsList()
    const results = await cmd.run(providerId, limit, offset, query)
    return results
  }

  /** Record that the signed-in user opened this engagement (MPN-1720). Never throws. */
  async recordEngagementView(engagementId: number, surface?: "MONITOR") {
    await new CommandRecordEngagementView().run(engagementId, surface)
  }

  async getLookUpList() {
    const cmd = new CommandLookUpList()
    const results = await cmd.run()
    return results
  }
  async getMotimaticUsers(engagementId: string) {
    const cmd = new CommandMotimaticUsers()
    const results = await cmd.run(engagementId)
    return results
  }

  async createTeamMember(engagementId: number, body: any) {
    const cmd = new CommandCreateTeamMember()
    const results = await cmd.run(engagementId, body)
    return results
  }

  async assignTeamMemberToEngagement(
    engagementId: number,
    employeeId: number,
    roleId: number,
  ) {
    const cmd = new CommandAssignTeamMemberToEngagement()
    const results = await cmd.run(engagementId, employeeId, roleId)
    return results
  }

  async getTeamRoles(orgType?: "CLIENT" | "PROVIDER") {
    const cmd = new CommandGetTeamRoles()
    const results = await cmd.run(orgType)
    return results
  }
  async getTeamMembers(engagementId?: number, roleCode?: string) {
    const cmd = new CommandGetTeamMembers()
    const results = await cmd.run(engagementId, roleCode)
    return results
  }

  async updateTeamMember(
    organizationId: number,
    employeeId: number,
    body: any,
  ) {
    const cmd = new CommandUpdateTeamMember()
    const results = await cmd.run(organizationId, employeeId, body)
    return results
  }

  async updateTeamMemberProfilePicture(
    organizationId: number,
    employeeId: number,
    profilePicture: File,
  ) {
    const cmd = new CommandUpdateTeamMemberProfilePicture()
    const results = await cmd.run(organizationId, employeeId, profilePicture)
    return results
  }

  async deleteTeamMember(employeeId: number, engagementId: number) {
    const cmd = new CommandDeleteTeamMember()
    const results = await cmd.run(employeeId, engagementId)
    return results
  }

  async createObjective(engagementId: number, name: string) {
    const cmd = new CommandCreateObjective()
    const results = await cmd.run(engagementId, name)
    return results
  }

  async updateObjective(
    engagementId: number,
    objectiveId: number,
    objective: Objective,
  ) {
    const cmd = new CommandUpdateObjective()
    const results = await cmd.run(engagementId, objectiveId, objective)
    return results
  }

  async getObjectives(engagementId: number) {
    const cmd = new CommandGetObjective()
    const results = await cmd.run(engagementId)
    return results
  }

  async getObjectiveListTypeChoices() {
    const cmd = new CommandGetObjectiveListTypeChoices()
    const results = await cmd.run()
    return results
  }

  async getObjectiveData(engagementId: number, objectiveId: number) {
    const cmd = new CommandGetObjectivesData()
    const results = await cmd.run(engagementId, objectiveId)
    return results
  }

  async updateEngagementData(
    engagementId: number,
    objectiveId: string,
    data: any,
  ) {
    const cmd = new CommandUpdateEngagementData()
    const results = await cmd.run(engagementId, objectiveId, data)
    return results
  }

  async updateKeyDates(
    engagementId: number,
    objectiveId: string,
    keyDateId: number,
    payload: { key_date_type_id?: number | null; label?: string | null; date: string | null },
  ) {
    const cmd = new CommandUpdateKeyDates()
    const results = await cmd.run(engagementId, objectiveId, keyDateId, payload)
    return results
  }

  async getKeyDates(engagementId: number, objectiveId: number) {
    const cmd = new CommandGetKeyDate()
    const results = await cmd.run(engagementId, objectiveId)
    return results
  }

  async deleteKeyDate(
    engagementId: number,
    objectiveId: number,
    keyDateId: number,
  ) {
    const cmd = new CommandDeleteKeyDate()
    const results = await cmd.run(engagementId, objectiveId, keyDateId)
    return results
  }

  async createKeyDates(
    engagementId: number,
    objectiveId: number,
    keyDate: KeyDate,
  ) {
    const cmd = new CommandCreateKeyDate()
    const results = await cmd.run(engagementId, objectiveId, keyDate)

    return results
  }

  async getKeyDateTypes() {
    const cmd = new CommandGetKeyDateTypes()
    const results = await cmd.run()
    return results
  }

  async deleteObjective(engagementId: number, objectiveId: number) {
    const cmd = new CommandObjectiveDelete()
    const results = await cmd.run(engagementId, objectiveId)
    return results
  }

  async getReferenceDataGroup() {
    const cmd = new CommandGetReferenceDataGroup()
    const results = await cmd.runKeyDateTypes()
    return results
  }

  async updateUseCaseSteps(
    engagementId: string,
    objectiveId: string,
    useCaseStep: UseCaseStep[],
  ) {
    const cmd = new CommandUseCaseSteps()
    const results = await cmd.run(engagementId, objectiveId, useCaseStep)
    return results
  }

  async getLaunches(engagementId: number) {
    const cmd = new CommandLaunches()
    const results = await cmd.run(engagementId)
    return results
  }

  async getEngagementAds(engagementId: number) {
    const cmd = new CommandGetEngagementAds()
    const results = await cmd.run(engagementId)
    return results
  }

  async updateEngagementAdStatus(
    engagementId: number,
    adId: number,
    status: EngagementAdStatus,
  ) {
    const cmd = new CommandUpdateEngagementAdStatus()
    const results = await cmd.run(engagementId, adId, status)
    return results
  }

  async createObjectiveJourneySteps(
    engagementId: number,
    objectiveId: number,
    journeySteps: any,
  ) {
    const cmd = new CommandCreateObjectiveJourneySteps()
    const results = await cmd.run(engagementId, objectiveId, journeySteps)
    return results
  }

  async getObjectiveJourneySteps(engagementId: number, objectiveId: number) {
    const cmd = new CommandGetObjectiveJourneySteps()
    const results = await cmd.run(engagementId, objectiveId)
    return results
  }

  async editObjectiveJourneyStep(
    engagementId: number,
    objectiveId: number,
    journeyId: number,
    modifiedJourneyStep: any,
  ) {
    const cmd = new CommandEditObjectiveJourneySteps()
    const results = await cmd.run(
      engagementId,
      objectiveId,
      journeyId,
      modifiedJourneyStep,
    )
    return results
  }

  async deleteObjectiveJourneySteps(
    engagementId: number,
    objectiveId: number,
    stepId: number,
  ) {
    const cmd = new CommandDeleteObjectiveJourneyStep()
    const results = await cmd.run(engagementId, objectiveId, stepId)
    return results
  }

  async getEngagementLaunches(engagementId: number) {
    const cmd = new CommandGetEngagementLaunches()
    const results = await cmd.run(engagementId)
    return results
  }

  async createEngagementLaunch(
    engagementId: number,
    name: string,
    objectiveId?: number,
    populationSegmentId?: number | null,
    populationSegmentExplicitNone = false,
  ) {
    const cmd = new CommandCreateEngagementLaunch()
    const results = await cmd.run(
      engagementId, name, objectiveId, populationSegmentId, populationSegmentExplicitNone,
    )
    return results
  }

  async setLaunchPopulationSegment(
    launchId: number,
    populationSegmentId: number | null,
    explicitNone = false,
  ) {
    const cmd = new CommandSetLaunchPopulationSegment()
    return await cmd.run(launchId, populationSegmentId, explicitNone)
  }

  /** Null clears the billing boundary (Danger Zone, MPN-1752). */
  async setLaunchGoLiveDate(launchId: number, goLiveDate: string | null) {
    return await new CommandSetLaunchGoLiveDate().run(launchId, goLiveDate)
  }

  async getSegmentLinks(engagementId: number) {
    const cmd = new CommandGetSegmentLinks()
    return await cmd.run(engagementId)
  }

  async getEngagementMetrics(engagementId: number) {
    const cmd = new CommandGetEngagementMetrics()
    const results = await cmd.run(engagementId)
    return results
  }

  async getEngagementAnalytics(engagementId: number, scope: AnalyticsScope = {}) {
    const cmd = new CommandGetEngagementAnalytics()
    const results = await cmd.run(engagementId, scope)
    return results
  }

  async getCampaignTable(engagementId: number) {
    const cmd = new CommandGetCampaignTable()
    return await cmd.run(engagementId)
  }

  async getEngagementAdTags(engagementId: number) {
    const cmd = new CommandGetEngagementAdTags()
    return await cmd.run(engagementId)
  }

  async getCampaignAudiences(engagementId: number, launchId: number) {
    const cmd = new CommandGetCampaignAudiences()
    return await cmd.run(engagementId, launchId)
  }

  async getEngagementLaunchAnalytics(engagementId: number, launchId: number) {
    const cmd = new CommandGetEngagementLaunchAnalytics();
    const results = await cmd.run(engagementId, launchId);
    return results;
  }

  async getEngagementStatus(engagementId: number) {
    const cmd = new CommandGetEngagementStatus()
    const results = await cmd.run(engagementId)
    return results
  }

  async updateEngagementStatus(engagementId: number, status: string) {
    const cmd = new CommandUpdateEngagementStatus()
    const results = await cmd.run(engagementId, status)
    return results
  }

  async getNetworkView(jobId: number): Promise<EngagementNetworkViewResponse> {
    const cmd = new CommandGetNetworkView()
    return await cmd.run(jobId)
  }

  async getEngagementTree(engagementId: number): Promise<EngagementTreeResponse> {
    const cmd = new CommandGetEngagementTree()
    return await cmd.run(engagementId)
  }

  async closeEngagement(engagementId: number) {
    const cmd = new CommandCloseEngagement()
    return await cmd.run(engagementId)
  }

  /**
   * Hide or restore an engagement for clients (MPN-1533).
   *
   * Distinct from `archiveEngagement` directly below, which archives objects on the ad
   * networks. Different action, different endpoint, different consequences.
   */
  async setEngagementArchived(engagementId: number, archived: boolean) {
    const cmd = new CommandSetEngagementArchived()
    return await cmd.run(engagementId, archived)
  }

  async archiveEngagement(engagementId: number, network?: string) {
    const cmd = new CommandArchiveEngagement()
    return await cmd.run(engagementId, network)
  }

  async publishEngagement(engagementId: number, options: PublishOptions = {}) {
    const job = await new CommandPublishEngagement().run(engagementId, options);
    addJob(job);
    return job;
  }

  // Campaign-level publish control (MPN-1364).
  async getPublishControl(engagementId: number) {
    return await new CommandGetPublishControl().run(engagementId);
  }

  async setLaunchReady(launchId: number, isReady: boolean) {
    return await new CommandSetLaunchReady().run(launchId, isReady);
  }

  async getPublishHistory(launchId: number) {
    return await new CommandGetPublishHistory().run(launchId);
  }

  async syncEngagement(engagementId: number) {
    const job = await new CommandSyncEngagement().run(engagementId);
    addJob(job);
    return job;
  }

  async startNetworkView(engagementId: number) {
    const job = await new CommandNetworkViewEngagement().run(engagementId);
    addJob(job);
    return job;
  }

  async importPerformance(days: number = 1) {
    const job = await new CommandImportPerformance().run(days);
    addJob(job);
    return job;
  }

  async getLastPerformanceImport(): Promise<LastPerformanceImport> {
    return new CommandGetLastPerformanceImport().run();
  }

  async getSetupStatus(engagementId: number): Promise<SetupStatusResponse> {
    const cmd = new CommandGetEngagementSetupStatus();
    return await cmd.run(engagementId);
  }

  async listConfigVersions(engagementId: number) {
    return new CommandListEngagementVersions().run(engagementId);
  }

  async countConfigVersions(engagementId: number) {
    return new CommandCountEngagementVersions().run(engagementId);
  }

  async getConfigVersionSnapshot(engagementId: number, versionId: number) {
    return new CommandGetEngagementVersionSnapshot().run(engagementId, versionId);
  }

  async getConfigActivity(engagementId: number) {
    return new CommandGetEngagementActivity().run(engagementId);
  }
}
